import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, Observable, of } from 'rxjs';
import { catchError, map, switchMap, takeWhile } from 'rxjs/operators';
import { RequestState } from '../../../shared/definitions/request-state.enum';
import { SimXTaxonomyTerm } from '../../../shared/models/entities/simx-taxonomy-term.model';
import { TaxonomyDataService } from '../../../shared/services/taxonomy-data.service';
import { Taxonomy } from '../definitions/taxonomy.enum';
import * as TaxonomyStoreActions from '../store/taxonomy-store.actions';
import * as TaxonomyStoreSelectors from '../store/taxonomy-store.selectors';

@Injectable({ providedIn: 'root' })
export class TaxonomyService {
  private readonly _store = inject(Store);
  private readonly _taxonomyDataService = inject(TaxonomyDataService);

  requestFetchAllTaxonomyTerms(): void {
    this._store.dispatch(TaxonomyStoreActions.requestTaxonomyTerms());
  }

  fetchAllTaxonomyTerms() {
    return this._taxonomyDataService.getAllTaxonomies().pipe(
      map(taxonomyTerms =>
        TaxonomyStoreActions.fetchTaxonomyTermsSucceeded({ taxonomyTerms }),
      ),
      catchError(() => of(TaxonomyStoreActions.fetchTaxonomyTermsFailed())),
    );
  }

  getTaxonomy(taxonomy: Taxonomy): Observable<Array<SimXTaxonomyTerm>> {
    return new Observable(observer => {
      this.requestFetchAllTaxonomyTerms();
      this._store
        .select(TaxonomyStoreSelectors.selectTaxonomyTermsRequestState)
        .pipe(
          takeWhile(
            requestState =>
              [RequestState.Initial, RequestState.InProgress].includes(
                requestState,
              ),
            true,
          ),
          filter(
            requestState =>
              ![RequestState.Initial, RequestState.InProgress].includes(
                requestState,
              ),
          ),
          switchMap(() =>
            this._store.select(TaxonomyStoreSelectors.selectTaxonomyTerms),
          ),
        )
        .subscribe(taxonomyTerms => {
          const requestedTaxonomy = taxonomyTerms.filter(
            taxonomyTerm => taxonomyTerm.taxonomy === taxonomy,
          );
          observer.next(requestedTaxonomy);
          observer.complete();
        });
    });
  }
}
